import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_MAP,
  JOB_CONTRACT_STATUSES,
  JOB_CONTRACT_STATUS_MAP,
  JOB_CONTRACT_WARN_DAYS,
  type EmploymentType,
  type JobContractStatus,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  SearchBox,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, money, num, plural, WORDS } from '../../lib/format';
import { useDebounced, useListState } from '../../lib/hooks';
import { useAuth } from '../../auth/AuthProvider';

interface ContractRow {
  id: string;
  number: string;
  status: JobContractStatus;
  statusLabel: string;
  statusKind: string;
  statusDescription: string;
  live: boolean;
  employmentType: string;
  employmentLabel: string;
  startsOn: string | null;
  endsOn: string | null;
  daysLeft: number | null;
  openEnded: boolean;
  probationMonths: number;
  baseSalary: number;
  currency: string;
  terms: string | null;
  signedOn: string | null;
  endReason: string | null;
  offerId: string | null;
  employee: { id: string; code: string | null; name: string; status: string };
}

interface ContractsSummary {
  draft: number;
  pending: number;
  signed: number;
  expiring: number;
  withoutContract: number;
}

interface EmployeeLite {
  id: string;
  name: string;
  code: string | null;
  status: string;
  baseSalary: string | number;
}

/**
 * عقود العمل.
 *
 * ## ولماذا العقدُ بابٌ لا ورقةٌ في ملفّ
 *
 * قبولُ العرض وعدٌ متبادَل، والعقدُ الموقَّعُ هو الالتزام. فمن قَبِل يُفتح
 * له ملفٌّ «مرشَّحٌ للتعيين» لا يدخل مرتّبًا ولا يُنتظر حضورُه، ولا يصير
 * على رأس العمل إلّا حين يُعلَّم عقدُه موقَّعًا. وبين الاثنين تقع أشياءُ
 * تُلغي التعيين — يُراجَع الطبُّ فيُردّ، وتصل شهادةٌ فلا تُطابق، أو يعتذر
 * صاحبُه لعرضٍ آخر.
 *
 * ## وما لا يقع هنا
 *
 * لا توقيعَ إلكترونيّ: لا ختمَ زمنيًّا معتمَدًا ولا شهادةَ توقيع. ما يُسجَّل
 * هنا واقعةٌ حدثت خارج النظام — وُقّع في يومٍ بعينه وسجّلها فلان. وزرٌّ
 * اسمه «وقّع إلكترونيًّا» في نظامٍ داخليٍّ ادّعاءٌ لا يصمد أمام أوّل نزاع.
 */
export function Contracts() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ContractRow | null>(null);
  const [deciding, setDeciding] = useState<{
    row: ContractRow;
    status: JobContractStatus;
  } | null>(null);

  const query = {
    status: state.status || undefined,
    expiring: state.expiring || undefined,
    q: debounced || undefined,
  };

  const list = useQuery({
    queryKey: ['contracts', query],
    queryFn: () => api<ContractRow[]>(`/hr/contracts${qs(query)}`),
  });

  const summary = useQuery({
    queryKey: ['contracts', 'summary'],
    queryFn: () => api<ContractsSummary>('/hr/contracts/summary'),
  });

  const rows = list.data ?? [];
  const s = summary.data;

  return (
    <div className="page">
      <PageHead
        title="عقود العمل"
        subtitle="العقدُ الموقَّعُ هو ما يجعل المرشَّحَ موظّفًا على رأس العمل"
        actions={
          canEdit && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              عقد جديد
            </button>
          )
        }
      />

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {s && (
        <div className="stats">
          <Stat
            label="بانتظار التوقيع"
            value={num(s.pending)}
            note={s.draft > 0 ? `و${num(s.draft)} مسوّدة` : 'لا مسوّدات'}
          />
          <Stat label="عقود سارية" value={num(s.signed)} />
          <Stat
            label="تنتهي قريبًا"
            value={num(s.expiring)}
            note={`خلال ${num(JOB_CONTRACT_WARN_DAYS)} يومًا أو انقضت`}
          />
          <Stat
            label="ملفّات بلا عقد"
            value={num(s.withoutContract)}
            note={
              s.withoutContract > 0
                ? 'مرشّحون للتعيين لم يُكتب لهم عقدٌ بعد'
                : 'كلُّ مرشَّحٍ له عقد'
            }
          />
        </div>
      )}

      {/*
        الملفُّ بلا عقدٍ أوّلُ ما يُعمل: صاحبُه قَبِل ويُنتظر، ولا شيءَ
        يتحرّك حتى يُكتب عقدُه. ويُقال بالاسم لا برقمٍ في بطاقة.
      */}
      {s && s.withoutContract > 0 && (
        <Notice title="ملفّاتٌ تنتظر عقدًا" icon="alert" kind="warning">
          {plural(s.withoutContract, WORDS.record)} في «ملفات المرشّحين
          للتعيين» بلا عقدٍ مكتوب. وصاحبُها قَبِل العرضَ وينتظر — ولا يصير
          على رأس العمل حتى يُوقَّع عقدُه.{' '}
          <Link className="link" to="/hr/employees/pending">
            افتح القائمة
          </Link>
          .
        </Notice>
      )}

      {s && s.expiring > 0 && (
        <Notice title="عقودٌ تنتهي قريبًا" icon="clock" kind="warning">
          {plural(s.expiring, WORDS.contract)} سارية تنتهي خلال{' '}
          {num(JOB_CONTRACT_WARN_DAYS)} يومًا أو انقضت مدّتُها ولم تُغلق. ومن
          يعمل بعقدٍ منقضٍ يعمل بلا عقد.{' '}
          <button
            className="link"
            type="button"
            onClick={() => patch({ expiring: '1', status: undefined })}
          >
            اعرضها
          </button>
          .
        </Notice>
      )}

      <div className="toolbar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث برقم العقد أو اسم الموظّف أو كوده"
        />

        <select
          className="select"
          value={state.status ?? ''}
          onChange={(e) =>
            patch({ status: e.target.value || undefined, expiring: undefined })
          }
        >
          <option value="">كل الحالات</option>
          {JOB_CONTRACT_STATUSES.map((k) => (
            <option key={k} value={k}>
              {JOB_CONTRACT_STATUS_MAP[k].label}
            </option>
          ))}
        </select>

        {state.expiring === '1' && (
          <button className="btn" onClick={() => patch({ expiring: undefined })}>
            <Icon name="x" size={14} />
            كل العقود
          </button>
        )}
      </div>

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا عقود"
          text="العقدُ يُكتب لمن فُتح له ملفٌّ — ومسوّدتُه تُكتب وحدَها حين يُفتح الملفُّ من عرضٍ مقبول."
          action={
            canEdit ? (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                اكتب عقدًا
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>العقد</th>
                  <th>الموظّف</th>
                  <th>النوع</th>
                  <th>المدة</th>
                  <th>الأجر</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className="cell-strong num" dir="ltr">
                        {c.number}
                      </span>
                      {c.signedOn && (
                        <div className="tiny mute">
                          وُقّع {date(c.signedOn)}
                        </div>
                      )}
                    </td>
                    <td>
                      <Link
                        className="link"
                        to={`/hr/employees/${c.employee.id}`}
                      >
                        {c.employee.name}
                      </Link>
                      <div className="tiny mute num">
                        {c.employee.code ?? '—'}
                      </div>
                    </td>
                    <td className="cell-mute">{c.employmentLabel}</td>
                    <td className="num cell-mute">
                      {date(c.startsOn ?? '')}
                      {c.openEnded ? (
                        <div className="tiny mute">غير محدّد المدّة</div>
                      ) : (
                        <>
                          <div className="tiny mute">
                            حتى {date(c.endsOn ?? '')}
                          </div>
                          {/*
                            «متبقٍّ» يُقال للسارية وحدَها: عقدٌ مسوّدةٌ
                            ينتهي بعد شهرٍ لا ينتهي — لم يبدأ.
                          */}
                          {c.status === 'SIGNED' &&
                            c.daysLeft !== null &&
                            c.daysLeft <= JOB_CONTRACT_WARN_DAYS && (
                              <div
                                className="tiny"
                                style={{ color: 'var(--warning)' }}
                              >
                                {c.daysLeft < 0
                                  ? `انقضى منذ ${num(-c.daysLeft)} يومًا`
                                  : `${num(c.daysLeft)} يومًا متبقّية`}
                              </div>
                            )}
                        </>
                      )}
                    </td>
                    <td className="num">{money(c.baseSalary)}</td>
                    <td>
                      <Badge kind={c.statusKind}>{c.statusLabel}</Badge>
                    </td>
                    <td>
                      {canEdit && (
                        <span className="row" style={{ gap: 4 }}>
                          {c.status === 'DRAFT' && (
                            <>
                              <button
                                className="btn btn-sm"
                                onClick={() => setEditing(c)}
                              >
                                تعديل
                              </button>
                              <button
                                className="btn btn-sm"
                                onClick={() =>
                                  setDeciding({ row: c, status: 'PENDING' })
                                }
                              >
                                أرسله للتوقيع
                              </button>
                            </>
                          )}
                          {c.status === 'PENDING' && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() =>
                                setDeciding({ row: c, status: 'SIGNED' })
                              }
                            >
                              <Icon name="check" size={12} />
                              تم التوقيع
                            </button>
                          )}
                          {/*
                            ولكلّ عقدٍ حيٍّ بابُ خروج.
                            كان الإلغاءُ متاحًا للموقَّع وحدَه، فمن انسحب قبل
                            أن يوقّع يبقى عقدُه مسوّدةً إلى الأبد — يُعدّ في
                            «بانتظار التوقيع» ويمنع كتابةَ عقدٍ لغيره.
                          */}
                          {(c.status === 'DRAFT' || c.status === 'PENDING') && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() =>
                                setDeciding({ row: c, status: 'TERMINATED' })
                              }
                            >
                              ألغِه
                            </button>
                          )}
                          {c.status === 'SIGNED' && (
                            <>
                              {/*
                                والمنقضي يُعلَّم بيد: لا مهمّةَ مجدولةً في هذا
                                النظام تُغلق العقودَ حين تمرّ مدّتُها، وزرٌّ
                                هنا أصدقُ من حالةٍ لا يبلغها شيء.
                              */}
                              {!c.openEnded &&
                                c.daysLeft !== null &&
                                c.daysLeft <= 0 && (
                                  <button
                                    className="btn btn-sm"
                                    onClick={() =>
                                      setDeciding({ row: c, status: 'EXPIRED' })
                                    }
                                  >
                                    انقضت مدّتُه
                                  </button>
                                )}
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() =>
                                  setDeciding({ row: c, status: 'TERMINATED' })
                                }
                              >
                                أنهِه
                              </button>
                            </>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/*
        الحدُّ يُكتب لا يُكتشف: النظامُ يسجّل واقعةَ التوقيع ولا يوقّع.
      */}
      <Notice title="النظام يسجّل التوقيع ولا يوقّع" icon="info">
        لا توقيعَ إلكترونيٌّ هنا: لا ختمَ زمنيًّا معتمَدًا ولا شهادةَ توقيع.
        «تم التوقيع» تسجيلُ واقعةٍ وقعت خارج النظام بتاريخها ومن سجّلها —
        والعقدُ الورقيُّ أو المعتمَد يبقى هو الحجّة.
      </Notice>

      {(creating || editing) && (
        <ContractDialog
          contract={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {deciding && (
        <StatusDialog
          row={deciding.row}
          status={deciding.status}
          onClose={() => setDeciding(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   تغيير الحالة
   ========================================================================== */

/**
 * نافذةُ التوقيع والإنهاء.
 *
 * التوقيعُ يطلب تاريخَه، والإنهاءُ يطلب سببَه: كلاهما واقعةٌ يُسأل عنها
 * بعد سنة. و«تم التوقيع» بلا تاريخٍ تجعل بدايةَ الخدمة مجهولةً — وهي ما
 * يُحتسب منه رصيدُ الإجازة وفترةُ الاختبار.
 */
function StatusDialog({
  row,
  status,
  onClose,
}: {
  row: ContractRow;
  status: JobContractStatus;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [signedOn, setSignedOn] = useState(
    row.signedOn ?? new Date().toISOString().slice(0, 10),
  );
  const [endReason, setEndReason] = useState('');

  const needsDate = status === 'SIGNED';
  const needsReason = status === 'TERMINATED';
  const valid =
    (!needsDate || signedOn !== '') && (!needsReason || endReason.trim() !== '');

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/contracts/${row.id}/status`, {
        method: 'POST',
        body: {
          status,
          ...(needsDate ? { signedOn } : {}),
          ...(needsReason ? { endReason: endReason.trim() } : {}),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast('حُفظت الحالة');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={`${JOB_CONTRACT_STATUS_MAP[status].label} — ${row.number}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className={status === 'TERMINATED' ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'تأكيد'}
          </button>
        </>
      }
    >
      <p className="mute">{JOB_CONTRACT_STATUS_MAP[status].description}</p>

      {status === 'SIGNED' && (
        <Notice title="ما يقع عند التوقيع" icon="info">
          {row.employee.name} يصير «على رأس العمل» من هذا التاريخ: يدخل دورةَ
          المرتّب، ويُنتظر حضورُه على خطّة عمله، ويُعدّ في القوى العاملة.
          ويصير تاريخُ التعيين في ملفّه هو تاريخَ بدء العقد —{' '}
          {date(row.startsOn ?? '')} — لا تاريخَ التوقيع: منه يُحتسب رصيدُ
          الإجازة وفترةُ الاختبار. ومن لم يبدأ بعد لا يظهر في ورقة الحضور حتى
          يبدأ.
        </Notice>
      )}

      {status === 'TERMINATED' && row.status === 'SIGNED' && (
        <Notice title="ما يقع عند الإنهاء" icon="alert" kind="warning">
          تنتهي خدمةُ {row.employee.name}: يخرج من دورة المرتّب ولا يُنتظر
          حضورُه. وقسائمُه السابقةُ تبقى — ما صُرف لا يُمحى.
        </Notice>
      )}

      {/*
        وإلغاءُ عقدٍ لم يُوقَّع لا يُنهي خدمةَ أحد: صاحبُه لم يبدأ أصلًا،
        ويبقى «مرشَّحًا للتعيين» حتى يُقرَّر في حقّه.
      */}
      {status === 'TERMINATED' && row.status !== 'SIGNED' && (
        <Notice title="إلغاءٌ قبل التوقيع" icon="info">
          يُغلَق هذا العقد ولا تُمسّ حالةُ {row.employee.name}: لم يبدأ العمل
          أصلًا، ويبقى «مرشَّحًا للتعيين» حتى يُكتب له عقدٌ آخر أو يُقرَّر في
          حقّه.
        </Notice>
      )}

      {status === 'EXPIRED' && (
        <Notice title="ما يقع عند انقضاء المدّة" icon="alert" kind="warning">
          تنتهي خدمةُ {row.employee.name} بانقضاء عقده. ومن أراد تجديدَه يكتب
          عقدًا جديدًا — العقدُ المنقضي لا يُبعث.
        </Notice>
      )}

      <div className="form-grid mt-4">
        {needsDate && (
          <div className="field span-2">
            <label className="label req" htmlFor="ct-signed">
              تاريخ التوقيع
            </label>
            <input
              id="ct-signed"
              className="input num"
              type="date"
              value={signedOn}
              onChange={(e) => setSignedOn(e.target.value)}
            />
            <span className="tiny mute">
              منه يُحتسب رصيدُ الإجازة وفترةُ الاختبار — فلا يُترك فارغًا.
            </span>
          </div>
        )}

        {needsReason && (
          <div className="field span-2">
            <label className="label req" htmlFor="ct-reason">
              سبب الإنهاء
            </label>
            <textarea
              id="ct-reason"
              className="input"
              rows={3}
              value={endReason}
              onChange={(e) => setEndReason(e.target.value)}
            />
            <span className="tiny mute">
              يُقرأ بعد سنةٍ حين يُسأل لماذا انتهت الخدمة — وإنهاءٌ بلا سببٍ
              مكتوبٍ لا يُراجَع.
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ==========================================================================
   نموذج العقد
   ========================================================================== */

function ContractDialog({
  contract,
  onClose,
}: {
  contract: ContractRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  /*
   * القائمةُ لمن يصحّ أن يُكتب له عقد.
   *
   * كانت أوّلَ مئتين من كلّ الملفّات بلا ترشيح — فتعرض من انتهت خدمتُه
   * وتُخفي من هو بعد الصفّ المئتين. والعقدُ يُكتب لمرشَّحٍ للتعيين أو لمن
   * على رأس العمل يُجدَّد له.
   */
  const pending = useQuery({
    queryKey: ['employees', { status: 'PENDING', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>('/hr/employees?status=PENDING&pageSize=200'),
    enabled: contract === null,
  });
  const onDuty = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>('/hr/employees?status=ACTIVE&pageSize=200'),
    enabled: contract === null,
  });
  const employees = {
    data: {
      items: [...(pending.data?.items ?? []), ...(onDuty.data?.items ?? [])],
    },
  };

  const [employeeId, setEmployeeId] = useState(contract?.employee.id ?? '');
  const [employmentType, setEmploymentType] = useState<EmploymentType>(
    (contract?.employmentType as EmploymentType) ?? 'FULL_TIME',
  );
  const [startsOn, setStartsOn] = useState(
    contract?.startsOn ?? new Date().toISOString().slice(0, 10),
  );
  const [endsOn, setEndsOn] = useState(contract?.endsOn ?? '');
  const [probationMonths, setProbationMonths] = useState(
    String(contract?.probationMonths ?? 3),
  );
  const [baseSalary, setBaseSalary] = useState(
    contract ? String(contract.baseSalary) : '',
  );
  const [terms, setTerms] = useState(contract?.terms ?? '');

  const backwards = endsOn !== '' && endsOn <= startsOn;
  const valid =
    (contract !== null || employeeId !== '') &&
    startsOn !== '' &&
    Number(baseSalary) > 0 &&
    !backwards;

  /* راتبُ الملفّ يُقترح على العقد الجديد: وهو ما اتُّفق عليه في العرض. */
  const picked = (employees.data?.items ?? []).find((e) => e.id === employeeId);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        employmentType,
        startsOn,
        endsOn: endsOn || null,
        probationMonths: Number(probationMonths) || 0,
        baseSalary: Number(baseSalary),
        terms: terms.trim() || null,
      };
      return contract
        ? api(`/hr/contracts/${contract.id}`, { method: 'PATCH', body })
        : api('/hr/contracts', {
            method: 'POST',
            body: { ...body, employeeId },
          });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast(contract ? 'حُفظ العقد' : 'كُتب العقد مسوّدةً');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={contract ? `تعديل ${contract.number}` : 'عقد عمل جديد'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        {contract ? (
          <div className="field span-2">
            <span className="label">الموظّف</span>
            <strong>{contract.employee.name}</strong>
            {/* صاحبُ العقد لا يُغيَّر: عقدٌ يُنقل من شخصٍ إلى شخصٍ ليس عقدًا. */}
            <span className="tiny mute">
              لا يُغيَّر — عقدٌ يُنقل من شخصٍ إلى آخر ليس عقدًا. اكتب عقدًا
              جديدًا لغيره.
            </span>
          </div>
        ) : (
          <div className="field span-2">
            <label className="label req" htmlFor="ct-emp">
              الموظّف
            </label>
            <select
              id="ct-emp"
              className="select"
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(e.target.value);
                const found = (employees.data?.items ?? []).find(
                  (x) => x.id === e.target.value,
                );
                if (found && baseSalary === '') {
                  setBaseSalary(String(Number(found.baseSalary)));
                }
              }}
            >
              <option value="">اختر</option>
              {(employees.data?.items ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                  {e.status === 'PENDING' ? ' — مرشَّح للتعيين' : ''}
                </option>
              ))}
            </select>
            {picked?.status === 'PENDING' && (
              <span className="tiny mute">
                توقيعُ هذا العقد يجعله على رأس العمل.
              </span>
            )}
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="ct-type">
            نوع التعاقد
          </label>
          <select
            id="ct-type"
            className="select"
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {EMPLOYMENT_TYPE_MAP[t].label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="ct-salary">
            الأجر المتّفق عليه
          </label>
          <input
            id="ct-salary"
            className="input num"
            type="number"
            min={0}
            value={baseSalary}
            onChange={(e) => setBaseSalary(e.target.value)}
          />
          {/*
            أجرُ العقد ليس راتبَ الملفّ إلى الأبد: العلاوةُ تُغيّر الراتب
            ولا تُغيّر ما وُقّع عليه — والفرقُ بينهما هو ما يُحتجّ به.
          */}
          <span className="tiny mute">
            ما وُقّع عليه. والعلاوةُ لاحقًا تغيّر راتبَ الملفّ ولا تغيّر هذا
            الرقم.
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="ct-from">
            يبدأ في
          </label>
          <input
            id="ct-from"
            className="input num"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ct-to">
            ينتهي في
          </label>
          <input
            id="ct-to"
            className="input num"
            type="date"
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
          <span className="tiny mute">
            اتركه فارغًا لعقدٍ غير محدّد المدّة.
          </span>
          {backwards && (
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              نهاية العقد قبل بدايته.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="ct-prob">
            فترة الاختبار بالأشهر
          </label>
          <input
            id="ct-prob"
            className="input num"
            type="number"
            min={0}
            max={12}
            value={probationMonths}
            onChange={(e) => setProbationMonths(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ct-terms">
            بنود إضافية
          </label>
          <textarea
            id="ct-terms"
            className="input"
            rows={6}
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            placeholder="ما لا يسع حقلًا: السرّية، عدم المنافسة، ملكية ما يُنتَج."
          />
        </div>
      </div>

      <p className="tiny mute mt-4">
        يُحفظ مسوّدةً. ولا أثرَ له على ملفّ الموظّف حتى يُعلَّم موقَّعًا —
        وحينها وحدَها يصير على رأس العمل.
      </p>
    </Modal>
  );
}
